import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    DndContext,
    DragOverlay,
    KeyboardSensor,
    PointerSensor,
    useDraggable,
    useSensor,
    useSensors,
    type DragEndEvent,
    type DragMoveEvent,
    type DragStartEvent,
} from '@dnd-kit/core';
import {
    Box,
    Button,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Save as SaveIcon,
    Close as CloseIcon,
    Visibility as VisibilityIcon,
    Delete as DeleteIcon,
} from '@mui/icons-material';
import BaseWidget, { WidgetSize } from './BaseWidget';
import {
    applyAdd,
    applyRemove,
    applyResize,
    buildRowBands,
    cellSpanWidth,
    columnWidth,
    commitPlacement,
    ensureCoordinates,
    placeWidget,
    pointerInsideGrid,
    positionsForLayout,
    snapColumn,
    snapRow,
    widthForSize,
    type PlacedWidget,
} from './dashboardLayout';

import ActionsRequiredWidget from './widgets/ActionsRequiredWidget';
import NextTimeOffWidget from './widgets/NextTimeOffWidget';
import KPIWidget from './widgets/KPIWidget';
import MyTimesheetWidget from './widgets/MyTimesheetWidget';
import TeamAvailabilityWidget from './widgets/TeamAvailabilityWidget';
import GalleryWidget from './widgets/GalleryWidget';
import RecentProjectsWidget from './widgets/RecentProjectsWidget';
import MyOpportunitiesWidget from './widgets/MyOpportunitiesWidget';
import WeekNumberWidget from './widgets/WeekNumberWidget';

export interface WidgetConfig {
    id: string;
    type: 'documents' | 'actions' | 'timeoff' | 'kpi' | 'timesheet' | 'availability' | 'gallery' | 'projects' | 'my_opportunities' | 'week';
    size: WidgetSize;
    title: string;
    x?: number;
    y?: number;
}

interface AvailableWidget {
    type: WidgetConfig['type'];
    title: string;
    defaultSize?: WidgetSize;
    allowedSizes?: WidgetSize[];
}

const AVAILABLE_WIDGETS: AvailableWidget[] = [
    { type: 'actions', title: 'Actions Required' },
    { type: 'timeoff', title: 'Next Time Off' },
    { type: 'kpi', title: 'Startups & KPIs' },
    { type: 'timesheet', title: 'My Timesheet' },
    { type: 'availability', title: 'Team Availability' },
    { type: 'gallery', title: 'Gallery' },
    { type: 'projects', title: 'Recent Projects' },
    { type: 'my_opportunities', title: 'My Leads' },
    { type: 'week', title: 'Week', defaultSize: 'small', allowedSizes: ['small'] },
];

const widgetMeta = (type: WidgetConfig['type']) =>
    AVAILABLE_WIDGETS.find((widget) => widget.type === type);

const resolveSize = (widget: WidgetConfig): WidgetSize => {
    const allowed = widgetMeta(widget.type)?.allowedSizes;
    if (allowed && !allowed.includes(widget.size)) return allowed[0];
    return widget.size;
};

const DEFAULT_LAYOUT: WidgetConfig[] = [
    { id: '1', type: 'actions', size: 'medium', title: 'Actions Required' },
    { id: '2', type: 'timeoff', size: 'small', title: 'Next Time Off' },
    { id: '4', type: 'kpi', size: 'small', title: 'Startups & KPIs' },
    { id: '8', type: 'week', size: 'small', title: 'Week' },
    { id: '5', type: 'timesheet', size: 'medium', title: 'My Timesheet' },
    { id: '6', type: 'availability', size: 'medium', title: 'Team Availability' },
    { id: '7', type: 'gallery', size: 'medium', title: 'Gallery' },
];

const LAYOUT_STORAGE_KEY = 'dashboard_layout';

const renderWidgetContent = (widget: WidgetConfig) => {
    switch (widget.type) {
        case 'actions':
            return <ActionsRequiredWidget />;
        case 'timeoff':
            return <NextTimeOffWidget />;
        case 'kpi':
            return <KPIWidget />;
        case 'timesheet':
            return <MyTimesheetWidget />;
        case 'availability':
            return <TeamAvailabilityWidget />;
        case 'gallery':
            return <GalleryWidget />;
        case 'projects':
            return <RecentProjectsWidget />;
        case 'my_opportunities':
            return <MyOpportunitiesWidget />;
        case 'week':
            return <WeekNumberWidget />;
        default:
            return <Typography>Unknown Widget Type</Typography>;
    }
};

type PlacedConfig = PlacedWidget<WidgetConfig>;

type DragMetrics = {
    layout: PlacedConfig[];
    gridRect: DOMRect;
    colWidth: number;
    gap: number;
    heights: Record<string, number>;
    bands: ReturnType<typeof buildRowBands>;
    pointer: { x: number; y: number };
    overlay: { width: number; height: number };
};

const pointerFromActivator = (event: DragStartEvent): { x: number; y: number } => {
    const native = event.activatorEvent;
    if ('clientX' in native) {
        return { x: (native as PointerEvent).clientX, y: (native as PointerEvent).clientY };
    }
    const node = document.querySelector(`[data-widget-id="${String(event.active.id)}"]`);
    const rect = node?.getBoundingClientRect();
    return rect
        ? { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 }
        : { x: 0, y: 0 };
};

interface DashboardWidgetProps {
    widget: PlacedConfig;
    isEditMode: boolean;
    isStacked: boolean;
    isActive: boolean;
    isDragging: boolean;
    reduceMotion: boolean;
    delta: { x: number; y: number };
    placeholderHeight?: number;
    onRemove: () => void;
    onResize: (size: WidgetSize) => void;
}

const DashboardWidget: React.FC<DashboardWidgetProps> = ({
    widget,
    isEditMode,
    isStacked,
    isActive,
    isDragging,
    reduceMotion,
    delta,
    placeholderHeight,
    onRemove,
    onResize,
}) => {
    const { attributes, listeners, setNodeRef } = useDraggable({
        id: widget.id,
        disabled: !isEditMode || isStacked,
    });
    const size = resolveSize(widget);
    const width = widthForSize(size);
    const allowedSizes = widgetMeta(widget.type)?.allowedSizes;
    const moving = isDragging && !isActive && (delta.x !== 0 || delta.y !== 0);

    return (
        <Box
            ref={setNodeRef}
            data-widget-id={widget.id}
            sx={{
                gridColumn: isStacked ? '1 / -1' : `${widget.x + 1} / span ${width}`,
                gridRow: isStacked ? 'auto' : String(widget.y + 1),
                transform: isDragging && !isActive ? `translate3d(${delta.x}px, ${delta.y}px, 0)` : 'none',
                transition: isDragging && !isActive && !reduceMotion ? 'transform 180ms ease-out' : 'none',
                zIndex: isActive ? 0 : moving ? 1 : 'auto',
                minWidth: 0,
            }}
        >
            {isActive ? (
                <Box
                    sx={{
                        height: placeholderHeight || '100%',
                        minHeight: placeholderHeight || 120,
                        border: '1px dashed',
                        borderColor: 'primary.main',
                        borderRadius: 2,
                        opacity: 0.35,
                    }}
                />
            ) : (
                <BaseWidget
                    title={widget.type === 'gallery' ? '' : widget.title}
                    size={size}
                    allowedSizes={allowedSizes}
                    isEditMode={isEditMode}
                    onRemove={onRemove}
                    onResize={onResize}
                    dragHandleProps={
                        isEditMode && !isStacked
                            ? ({ ...(listeners ?? {}), ...attributes } as React.HTMLAttributes<HTMLDivElement>)
                            : undefined
                    }
                >
                    {renderWidgetContent(widget)}
                </BaseWidget>
            )}
        </Box>
    );
};

const DashboardGrid: React.FC = () => {
    const theme = useTheme();
    const isStacked = useMediaQuery(theme.breakpoints.down('sm'));
    const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
    const gridRef = useRef<HTMLDivElement | null>(null);
    const dragMetricsRef = useRef<DragMetrics | null>(null);
    const lastSnapRef = useRef<{ x: number; y: number; inside: boolean } | null>(null);

    const [widgets, setWidgets] = useState<PlacedConfig[]>([]);
    const [isEditMode, setIsEditMode] = useState(false);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [activeId, setActiveId] = useState<string | null>(null);
    const [preview, setPreview] = useState<PlacedConfig[] | null>(null);
    const [snap, setSnap] = useState<{ x: number; y: number } | null>(null);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(KeyboardSensor)
    );

    useEffect(() => {
        const savedLayout = localStorage.getItem(LAYOUT_STORAGE_KEY);
        let next: WidgetConfig[] = DEFAULT_LAYOUT;
        if (savedLayout) {
            try {
                next = JSON.parse(savedLayout);
            } catch (e) {
                console.error('Failed to parse dashboard layout', e);
            }
        }
        const laidOut = ensureCoordinates(next.map((widget) => ({ ...widget, size: resolveSize(widget) })));
        setWidgets(laidOut);
        localStorage.setItem(LAYOUT_STORAGE_KEY, JSON.stringify(laidOut));
    }, []);

    const saveLayout = (newLayout: PlacedConfig[]) => {
        setWidgets(newLayout);
        localStorage.setItem(LAYOUT_STORAGE_KEY, JSON.stringify(newLayout));
    };

    const handleResize = (id: string, newSize: WidgetSize) => {
        saveLayout(applyResize(widgets, id, newSize));
    };

    const handleRemove = (id: string) => {
        saveLayout(applyRemove(widgets, id));
    };

    const handleAddWidget = (type: WidgetConfig['type'], title: string, size: WidgetSize = 'medium') => {
        const newWidget: WidgetConfig = {
            id: Date.now().toString(),
            type,
            size,
            title,
        };
        saveLayout(applyAdd(widgets, newWidget));
    };

    const clearDrag = () => {
        setActiveId(null);
        setPreview(null);
        setSnap(null);
        dragMetricsRef.current = null;
        lastSnapRef.current = null;
    };

    const handleDragStart = (event: DragStartEvent) => {
        const grid = gridRef.current;
        if (!grid) return;
        const layout = ensureCoordinates(widgets);
        const gridRect = grid.getBoundingClientRect();
        const styles = getComputedStyle(grid);
        const gap = parseFloat(styles.rowGap || styles.gap) || 24;
        const colWidth = columnWidth(gridRect.width, gap);
        const heights: Record<string, number> = {};
        const rects: Record<string, { top: number; bottom: number; width: number; height: number }> = {};
        grid.querySelectorAll('[data-widget-id]').forEach((node) => {
            const id = node.getAttribute('data-widget-id');
            if (!id) return;
            const rect = node.getBoundingClientRect();
            heights[id] = rect.height;
            rects[id] = { top: rect.top, bottom: rect.bottom, width: rect.width, height: rect.height };
        });
        const bands = buildRowBands(layout, rects);

        const active = String(event.active.id);
        const overlayRect = rects[active];
        const activeWidget = layout.find((widget) => widget.id === active);
        dragMetricsRef.current = {
            layout,
            gridRect,
            colWidth,
            gap,
            heights,
            bands,
            pointer: pointerFromActivator(event),
            overlay: {
                width: overlayRect?.width
                    || (activeWidget
                        ? cellSpanWidth(widthForSize(resolveSize(activeWidget)), colWidth, gap)
                        : 0),
                height: overlayRect?.height || 160,
            },
        };
        setActiveId(active);
        setPreview(layout);
        setSnap({
            x: layout.find((widget) => widget.id === active)?.x ?? 0,
            y: layout.find((widget) => widget.id === active)?.y ?? 0,
        });
        lastSnapRef.current = {
            x: layout.find((widget) => widget.id === active)?.x ?? 0,
            y: layout.find((widget) => widget.id === active)?.y ?? 0,
            inside: true,
        };
    };

    const handleDragMove = (event: DragMoveEvent) => {
        const metrics = dragMetricsRef.current;
        if (!metrics) return;
        const pointer = {
            x: metrics.pointer.x + event.delta.x,
            y: metrics.pointer.y + event.delta.y,
        };
        const widget = metrics.layout.find((item) => item.id === String(event.active.id));
        if (!widget) return;
        const width = widthForSize(resolveSize(widget));
        const inside = pointerInsideGrid(pointer, metrics.gridRect);
        if (!inside) {
            if (lastSnapRef.current?.inside !== false) {
                lastSnapRef.current = { x: widget.x, y: widget.y, inside: false };
                setSnap(null);
                setPreview(null);
            }
            return;
        }
        const x = snapColumn(pointer.x, metrics.gridRect.left, metrics.gridRect.width, width);
        const y = snapRow(pointer.y, metrics.bands);
        if (lastSnapRef.current?.inside && lastSnapRef.current.x === x && lastSnapRef.current.y === y) {
            return;
        }
        lastSnapRef.current = { x, y, inside: true };
        setSnap({ x, y });
        setPreview(placeWidget(metrics.layout, widget.id, x, y));
    };

    const handleDragEnd = (_event: DragEndEvent) => {
        const metrics = dragMetricsRef.current;
        const last = lastSnapRef.current;
        if (metrics && last?.inside) {
            saveLayout(commitPlacement(metrics.layout, String(_event.active.id), last.x, last.y));
        }
        clearDrag();
    };

    const handleDragCancel = () => {
        clearDrag();
    };

    const activeWidget = widgets.find((widget) => widget.id === activeId) ?? null;
    const overlaySize = dragMetricsRef.current?.overlay;

    const deltas = useMemo(() => {
        const metrics = dragMetricsRef.current;
        if (!metrics || !preview || !activeId) return {} as Record<string, { x: number; y: number }>;
        const basePos = positionsForLayout(metrics.layout, metrics.colWidth, metrics.gap, metrics.heights);
        const previewPos = positionsForLayout(preview, metrics.colWidth, metrics.gap, metrics.heights);
        const next: Record<string, { x: number; y: number }> = {};
        for (const widget of metrics.layout) {
            if (widget.id === activeId) {
                next[widget.id] = { x: 0, y: 0 };
                continue;
            }
            const from = basePos[widget.id];
            const to = previewPos[widget.id];
            next[widget.id] = from && to
                ? { x: to.left - from.left, y: to.top - from.top }
                : { x: 0, y: 0 };
        }
        return next;
    }, [preview, activeId]);

    const outline = useMemo(() => {
        const metrics = dragMetricsRef.current;
        if (!metrics || !preview || !activeId || !snap) return null;
        const previewPos = positionsForLayout(preview, metrics.colWidth, metrics.gap, metrics.heights);
        return previewPos[activeId] ?? null;
    }, [preview, activeId, snap]);

    const sortedWidgets = useMemo(
        () => [...widgets].sort((a, b) => a.y - b.y || a.x - b.x),
        [widgets]
    );

    return (
        <Box sx={{ width: '100%', mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5" color="text.secondary">
                    {isEditMode ? 'Customizing Dashboard' : ''}
                </Typography>
                <Box>
                    {!isEditMode ? (
                        <Button
                            startIcon={<EditIcon />}
                            onClick={() => setIsEditMode(true)}
                            variant="outlined"
                            size="small"
                        >
                            Customize
                        </Button>
                    ) : (
                        <>
                            <Button
                                startIcon={<AddIcon />}
                                onClick={() => setIsDrawerOpen(true)}
                                variant="contained"
                                color="secondary"
                                size="small"
                                sx={{ mr: 1 }}
                            >
                                Add Widget
                            </Button>
                            <Button
                                startIcon={<SaveIcon />}
                                onClick={() => setIsEditMode(false)}
                                variant="contained"
                                size="small"
                            >
                                Done
                            </Button>
                        </>
                    )}
                </Box>
            </Box>

            <DndContext
                sensors={sensors}
                onDragStart={handleDragStart}
                onDragMove={handleDragMove}
                onDragEnd={handleDragEnd}
                onDragCancel={handleDragCancel}
            >
                <Box
                    ref={gridRef}
                    sx={{
                        position: 'relative',
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: '1fr',
                            sm: 'repeat(12, minmax(0, 1fr))',
                        },
                        gap: 3,
                        alignItems: 'start',
                    }}
                >
                    {sortedWidgets.map((widget) => (
                        <DashboardWidget
                            key={widget.id}
                            widget={widget}
                            isEditMode={isEditMode}
                            isStacked={isStacked}
                            isActive={activeId === widget.id}
                            isDragging={Boolean(activeId)}
                            reduceMotion={reduceMotion}
                            delta={deltas[widget.id] ?? { x: 0, y: 0 }}
                            placeholderHeight={dragMetricsRef.current?.heights[widget.id]}
                            onRemove={() => handleRemove(widget.id)}
                            onResize={(newSize) => handleResize(widget.id, newSize)}
                        />
                    ))}
                    {outline && snap && (snap.x !== activeWidget?.x || snap.y !== activeWidget?.y) && (
                        <Box
                            aria-hidden
                            sx={{
                                position: 'absolute',
                                left: outline.left,
                                top: outline.top,
                                width: outline.width,
                                height: outline.height,
                                border: '2px dashed',
                                borderColor: 'primary.main',
                                borderRadius: 2,
                                pointerEvents: 'none',
                                zIndex: 2,
                                boxSizing: 'border-box',
                            }}
                        />
                    )}
                </Box>
                <DragOverlay dropAnimation={null} adjustScale={false}>
                    {activeWidget && (
                        <Box
                            sx={{
                                width: overlaySize?.width,
                                height: overlaySize?.height,
                                cursor: 'grabbing',
                                borderRadius: 2,
                                overflow: 'hidden',
                            }}
                        >
                            <BaseWidget
                                title={activeWidget.type === 'gallery' ? '' : activeWidget.title}
                                size={resolveSize(activeWidget)}
                                isEditMode={false}
                                elevated
                            >
                                {renderWidgetContent(activeWidget)}
                            </BaseWidget>
                        </Box>
                    )}
                </DragOverlay>
            </DndContext>

            <Drawer
                anchor="right"
                open={isDrawerOpen}
                onClose={() => setIsDrawerOpen(false)}
            >
                <Box sx={{ width: 300, p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6">Add Widget</Typography>
                        <IconButton onClick={() => setIsDrawerOpen(false)}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    <List>
                        {AVAILABLE_WIDGETS.map((w, index) => {
                            const isAdded = widgets.some((widget) => widget.type === w.type);
                            return (
                                <ListItem
                                    button
                                    key={index}
                                    onClick={() => {
                                        if (isAdded) {
                                            const target = widgets.find((widget) => widget.type === w.type);
                                            if (target) saveLayout(applyRemove(widgets, target.id));
                                        } else {
                                            handleAddWidget(w.type, w.title, w.defaultSize ?? 'medium');
                                        }
                                    }}
                                >
                                    <ListItemIcon>
                                        {isAdded ? <DeleteIcon color="error" /> : <VisibilityIcon color="primary" />}
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={w.title}
                                        secondary={isAdded ? 'Click to remove' : 'Click to add'}
                                    />
                                </ListItem>
                            );
                        })}
                    </List>
                </Box>
            </Drawer>
        </Box>
    );
};

export default DashboardGrid;
